/**
 * The settings tab for a practice's premium IOL prices, and the address patients reply to with their choice
 * (the pre-op step's "CC to practice"). A price entered here is used by every surgeon at the practice; a lens
 * left empty takes each surgeon's own price - whatever they last sent a patient. These fill in the Premium IOL
 * options on a patient's pre-op plan, where they can still be changed for that patient - sending never saves
 * them back here.
 *
 * Ophthalmologists and practice admins change them; registrars see them read only. Saved with
 * PremiumLensAPI.savePracticePrices, which replaces the practice's whole premium lens section - so anything
 * stored that this screen does not show is sent back unchanged rather than dropped.
 */
app.component("premiumLensPricesSetting", {
	templateUrl: "app/src/js/components/premiumLensPrices/premiumLensPricesSetting.html",
	controller: "premiumLensPricesSettingController",
	bindings: { practiceId: "<" },
});

app.controller("premiumLensPricesSettingController", [
	"$scope",
	"PremiumLensAPI",
	"Session",
	"PREMIUM_LENSES",
	"PremiumLensPriceList",
	function ($scope, PremiumLensAPI, Session, PREMIUM_LENSES, PriceList) {
		const $ctrl = this;
		const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

		$scope.MAX_PRICE = PriceList.MAX_PRICE;
		$scope.toricLenses = PREMIUM_LENSES.filter((lens) => lens.toricOnly);
		$scope.premiumLenses = PREMIUM_LENSES.filter((lens) => !lens.toricOnly);
		// Who can change the practice's prices; set once the practice is known.
		$scope.canEdit = false;
		$scope.state = { loading: false, error: null, saving: false };
		$scope.saveStatus = "";
		$scope.form = null;

		// What was last loaded or saved, to tell whether there is anything to save
		let saved = null;
		// Saved entries this screen doesn't list, and each price's id - both sent back on save
		let unlisted = [];
		let ids = {};

		const snapshot = () => angular.toJson($scope.form);

		$ctrl.$onInit = function () {
			$scope.practiceId = $ctrl.practiceId || (Session.practice && Session.practice.id);
			const practice = (Session.practiceOptions || []).find((p) => p.id === $scope.practiceId) || Session.practice || {};
			$scope.practiceName = practice.name || "";
			$scope.canEdit = Session.hasPermissionAt(practice, "SURGERY_PREMIUM_LENS_PRICE_MANAGE");
			load();
		};

		function load() {
			$scope.state.loading = true;
			$scope.state.error = null;
			$scope.form = null;
			PremiumLensAPI.getPracticePrices($scope.practiceId)
				.then((resp) => adopt(resp.data || {}))
				.catch((resp) => ($scope.state.error = PriceList.errorMessage(resp, "The practice's prices couldn't be loaded.")))
				.finally(() => ($scope.state.loading = false));
		}

		$scope.reload = load;

		function adopt(data) {
			const own = PriceList.fromList(data.premium_lenses_saved_prices);
			ids = own.ids;
			unlisted = own.unlisted;
			$scope.form = { prices: own.prices, email: data.premium_lenses_response_email || "" };
			saved = snapshot();
		}

		$scope.isDirty = () => !!$scope.form && snapshot() !== saved;
		$scope.$watch($scope.isDirty, function (dirty) {
			if (dirty && $scope.saveStatus.indexOf("Error") !== 0) {
				$scope.saveStatus = "";
			}
		});

		$scope.hasPrice = PriceList.hasPrice;
		$scope.isPriceInvalid = PriceList.isPriceInvalid;

		// The first thing stopping a save, in words; empty when there is nothing wrong.
		$scope.problem = function () {
			if (!$scope.form) {
				return "";
			}
			const priceProblem = PriceList.priceProblem($scope.form.prices);
			if (priceProblem) {
				return priceProblem;
			}
			const email = ($scope.form.email || "").trim();
			if (email && !EMAIL_PATTERN.test(email)) {
				return "That email address doesn't look right.";
			}
			return "";
		};

		$scope.ownCount = function () {
			return $scope.form ? PriceList.count($scope.form.prices) : 0;
		};

		$scope.undo = function () {
			$scope.form = angular.fromJson(saved);
			$scope.saveStatus = "";
		};

		$scope.save = function () {
			if (!$scope.canEdit || !$scope.isDirty() || $scope.problem() || $scope.state.saving) {
				return;
			}
			$scope.state.saving = true;
			$scope.saveStatus = "Saving...";
			const body = {
				premium_lenses_saved_prices: PriceList.toList($scope.form.prices, ids, unlisted),
				premium_lenses_response_email: ($scope.form.email || "").trim(),
			};
			PremiumLensAPI.savePracticePrices($scope.practiceId, body)
				.then(function (resp) {
					adopt(resp.data || {});
					$scope.saveStatus = "Prices saved";
				})
				.catch((resp) => ($scope.saveStatus = "Error: " + PriceList.errorMessage(resp, "the prices couldn't be saved.")))
				.finally(() => ($scope.state.saving = false));
		};
	},
]);
